// Device detection utilities for responsive settings

export class DeviceDetection {
  // Cache the initial device detection to prevent switching
  static _initialDeviceType = null;
  
  /**
   * Detect if device is mobile on first call, then cache the result
   */
  static _detectInitialDeviceType() {
    // Dev override for testing layouts: localStorage.setItem('musiker-force-device', 'mobile' | 'desktop')
    try {
      const forced = localStorage.getItem('musiker-force-device');
      if (forced === 'mobile' || forced === 'desktop') {
        return forced;
      }
    } catch (e) { /* localStorage unavailable */ }

    // Check user agent first (most reliable)
    const mobileUserAgents = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUserAgent = mobileUserAgents.test(navigator.userAgent);
    
    if (isMobileUserAgent) {
      return 'mobile';
    }
    
    // Check for touch capability
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    
    // Check for orientation API (mobile-specific)
    const hasOrientation = 'orientation' in window;
    
    // If has touch AND orientation, likely mobile even without mobile user agent
    if (hasTouch && hasOrientation) {
      return 'mobile';
    }
    
    // Default to desktop
    return 'desktop';
  }
  
  /**
   * Check if the current device is mobile (cached result)
   */
  static isMobile() {
    if (this._initialDeviceType === null) {
      this._initialDeviceType = this._detectInitialDeviceType();
      // console.log('Initial device type detected and cached:', this._initialDeviceType);
    }
    return this._initialDeviceType === 'mobile';
  }
  
  /**
   * Check if the current device is in portrait orientation
   */
  static isPortrait() {
    return window.innerHeight > window.innerWidth;
  }
  
  /**
   * Check if the current device is in landscape orientation
   */
  static isLandscape() {
    return window.innerWidth > window.innerHeight;
  }
  
  /**
   * Get device type string for settings differentiation
   */
  static getDeviceType() {
    if (this.isMobile()) {
      return this.isPortrait() ? 'mobile-portrait' : 'mobile-landscape';
    }
    return 'desktop';
  }
  
  /**
   * Get simplified device category for settings
   */
  static getDeviceCategory() {
    return this.isMobile() ? 'mobile' : 'desktop';
  }
  
  /**
   * Get screen dimensions info
   */
  static getScreenInfo() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      orientation: this.isPortrait() ? 'portrait' : 'landscape',
      isMobile: this.isMobile(),
      deviceType: this.getDeviceType(),
      category: this.getDeviceCategory()
    };
  }
  
  /**
   * Listen for orientation and resize changes
   */
  static addOrientationListener(callback) {
    const handler = () => {
      // Small delay to ensure the orientation change is complete
      setTimeout(() => {
        callback(this.getScreenInfo());
      }, 100);
    };
    
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }
  
  /**
   * Get settings key suffix based on device type
   */
  static getSettingsKeySuffix() {
    const category = this.getDeviceCategory();
    return category === 'mobile' ? '_mobile' : '_desktop';
  }
}